import { computed, type ElementRef, EventEmitter, inject, Injectable, signal } from '@angular/core';
import { type EscapeClosable, EscapeHandler } from '@reconcept-platform/shared/util';
import { type Observable, ReplaySubject, type Subscription } from 'rxjs';
import { CoreModalService } from '../core-modal.service';
import { type IModalWindowController, type ModalInstance } from '../modal.types';
import { ModalWindowCompareController } from './modal-window-compare.control';

@Injectable()
export class ModalWindowController implements EscapeClosable, IModalWindowController {
  private modalService = inject(CoreModalService);
  private escapeHandler = inject(EscapeHandler);
  private modalCompare = inject(ModalWindowCompareController);

  displayAsSidebar = false;

  // needs 2 properties, because for linkedSignal we need Angular 19
  displayMinimized = computed(() => this._displayMinimized() && !this.modalCompare.isComparing());
  _displayMinimized = signal(false);

  // @ts-expect-error TS2345 argument not assignable
  title = signal<string>(undefined);
  editing = signal<boolean>(false);

  scrollableBody: ElementRef;

  private modalInstance: ModalInstance;

  private beforeDismiss: () => Observable<boolean>;
  private presetModalResult: any;

  private confirmDismissSubject: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  private parentDismissed: Subscription | undefined;

  dismissed: EventEmitter<any> = new EventEmitter();

  init(modalInstance: ModalInstance) {
    this.modalInstance = modalInstance;

    // a modal opened from another modal window can't outlive it, nor show up in the next one
    this.parentDismissed = this.modalInstance.parentController?.dismissed.subscribe(() => this.close(undefined));

    // if no background close is allowed, also ignore escape to close
    // allow undefined too, only explicit false should be ignored
    if (this.modalInstance.config.allowBackgroundClose !== false) {
      this.escapeHandler.register(this);
    }

    this.modalCompare.init(modalInstance);
  }

  isMinimizedAllowed() {
    return this.modalInstance.config.allowMinimize;
  }

  isCompareAllowed() {
    return this.modalInstance.config.allowCompare;
  }

  closeByEscape() {
    if (!this.displayAsSidebar && !this.displayMinimized()) {
      this.dismiss();
    }
  }

  /**
   * If you want to close the modal and have subscribers processed, but don't care about the parameter
   */
  dismissAndProcess() {
    this.dismiss(true);
  }

  /**
   * if result === undefined, then presetModalResult is returned, which can be undefined.
   * if result === null, then null is returned. This happens when:
   *    - overlay is clicked
   *    - ionic disclaimer modal is closed
   *    - guidelines workspace-selector modal is closed
   *    - residency is deleted
   * if result is any value, then that value is returned
   *
   * @param result
   * @param ignoreBeforeCloseCallback
   */
  dismiss(result?: any, ignoreBeforeCloseCallback?: boolean) {
    if (!!this.beforeDismiss && !ignoreBeforeCloseCallback) {
      // execute the method and subscribe to the closeSubject. Should return true to close the modal.
      this.beforeDismiss();

      this.confirmDismissSubject.subscribe((confirmedClose) => {
        // if confirmed, close
        if (confirmedClose) {
          // null is a valid result
          this.closeModal(result);
        }
      });
    } else {
      // close directly
      // null is a valid result
      this.closeModal(result);
    }
  }

  dismissParent(result?: any) {
    if (this.modalInstance.parentController) {
      this.modalInstance.parentController.dismiss(result, true);
    }
  }

  registerScrollableBody(element: ElementRef) {
    this.scrollableBody = element;
  }

  registerBeforeDismiss(method: () => any) {
    this.beforeDismiss = method;
  }

  scrollToTop() {
    if (this.scrollableBody) {
      // scroll to top; use scrollTop over scrollTo() for IE 11 reasons
      this.scrollableBody.nativeElement.scrollTop = 0;
    }
  }

  scrollParentToTop() {
    if (this.modalInstance.parentController) {
      this.modalInstance.parentController.scrollToTop();
    }
  }

  // display modes
  showModalNormal() {
    // unset modes
    this._displayMinimized.set(false);
    this.displayAsSidebar = false;
  }

  toggleDisplayMinimized() {
    // toggle display
    this._displayMinimized.set(!this.displayMinimized());
  }

  toggleDisplayAsSidebar() {
    // toggle display
    this.displayAsSidebar = !this.displayAsSidebar;

    // unset minimized
    this._displayMinimized.set(false);
  }

  setDisplayAsSidebar() {
    this.displayAsSidebar = true;

    // unset minimized
    this._displayMinimized.set(false);
  }

  confirmDismiss() {
    this.confirmDismissSubject.next(true);
  }

  openPrintWindow(path: string): void {
    const host =
      window.location.protocol +
      '//' +
      window.location.hostname +
      (window.location.port ? ':' + window.location.port : '');

    window.open(host + '/print/' + path, '_blank', 'top=0,left=0,height=100%,width=auto');
  }

  setModalResult(result: any) {
    this.presetModalResult = result;
  }

  setBiggerModal() {
    this.modalInstance.config.style = 'advanced';
  }

  setComparingMode(compare: boolean) {
    this.modalCompare.setComparingMode(compare, this.modalService.modals());
  }

  isComparingMode() {
    return this.modalCompare.isComparingMode();
  }

  startCompare() {
    return this.modalCompare.startCompare();
  }

  private closeModal(result: any) {
    this.close(result !== undefined ? result : this.presetModalResult);
  }

  private close(result: any) {
    this.parentDismissed?.unsubscribe();
    this.modalService.closeModal(result, this.modalInstance);
    this.escapeHandler.unregister(this);
    this.dismissed.emit(result);
    this.dismissed.complete();
  }
}
